import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { AlertTriangle, ChevronDown, ExternalLink, Search, Unlink } from "lucide-react";
import api from "../../lib/api";
import { recordsError } from "../../lib/records";
import { changedCount, clauseBlockOf, clauseLines, lineDiff, updateAvailable } from "../../lib/clauses";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Skeleton } from "../ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../ui/alert-dialog";

// The text of a clause, line by line — the same everywhere a clause is shown (picker, card, comparison).
export function ClauseText({ blocks, testid }) {
  const lines = clauseLines(blocks);
  if (!lines.length) return <p className="text-sm text-slate-500">Cette version ne contient aucun texte.</p>;
  return (
    <ul className="space-y-1 text-sm text-slate-700" data-testid={testid}>
      {lines.map((l, i) => (
        <li key={i} className={l.kind === "article" || l.kind === "heading" ? "font-semibold text-slate-900" : ""}>{l.text}</li>
      ))}
    </ul>
  );
}

// A « clause » block inside a template: the version it took and its frozen copy. Locked — only the library edits a clause.
export function ClauseCard({ block, index, status, onUpdate, onDetach }) {
  const [open, setOpen] = useState(false);
  const newer = updateAvailable(block, status);
  return (
    <div className="space-y-2" data-testid={`template-clause-${index}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-slate-900 break-words" data-testid={`template-clause-${index}-name`}>{status?.name || block.clause_name}</span>
        <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-600" data-testid={`template-clause-${index}-version`}>Version {block.clause_number}</span>
        {newer && (
          <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800" data-testid={`template-clause-${index}-update`}>
            <AlertTriangle size={12} aria-hidden="true" />Version {status.latest_number} disponible
          </span>
        )}
        {status && !status.active && <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs text-slate-500">Désactivée dans la bibliothèque</span>}
      </div>
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} data-testid={`template-clause-${index}-toggle`}
        className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700">
        <ChevronDown size={13} className={`transition-transform ${open ? "rotate-180" : ""}`} />{open ? "Masquer le texte" : "Voir le texte figé"}
      </button>
      {open && <div className="rounded-md bg-slate-50 p-3"><ClauseText blocks={block.clause_blocks} testid={`template-clause-${index}-text`} /></div>}
      <p className="text-xs text-slate-500">Copie figée de cette version : une nouvelle version de la clause ne change rien tant que vous ne la reprenez pas.</p>
      <div className="flex flex-wrap gap-2">
        {newer && <Button size="sm" onClick={onUpdate} data-testid={`template-clause-${index}-compare`}>Mettre à jour…</Button>}
        <Button variant="outline" size="sm" onClick={onDetach} data-testid={`template-clause-${index}-detach`}><Unlink size={13} className="mr-1.5" />Détacher</Button>
        <Button variant="ghost" size="sm" asChild><Link to={`/documents/clauses/${block.clause_id}`} data-testid={`template-clause-${index}-open`}><ExternalLink size={13} className="mr-1.5" />Voir dans la bibliothèque</Link></Button>
      </div>
    </div>
  );
}

// « Ajouter un bloc › Clause de la bibliothèque »: search, theme, the published text, the version that will be taken.
export function ClausePicker({ open, onClose, onPick }) {
  const [themes, setThemes] = useState([]);
  const [items, setItems] = useState(null);
  const [search, setSearch] = useState("");
  const [theme, setTheme] = useState("all");
  const [chosen, setChosen] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSearch(""); setTheme("all"); setChosen(null); setError(""); setItems(null);
    api.get("/records/clause-themes").then(({ data }) => setThemes(data.items)).catch(() => {});
  }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => {
      api.get("/records/clauses", { params: { search: search || undefined, theme: theme === "all" ? undefined : theme } })
        .then(({ data }) => setItems(data.items)).catch((e) => setError(recordsError(e, "Impossible de charger la bibliothèque.")));
    }, 200);
    return () => clearTimeout(t);
  }, [open, search, theme]);

  const choose = async (clause) => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.get(`/records/clauses/${clause.id}`);
      setChosen(data);
    } catch (e) {
      setError(recordsError(e, "Clause introuvable."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Clause de la bibliothèque</DialogTitle>
          <DialogDescription>Le modèle garde une copie figée de la version publiée que vous choisissez.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3" data-testid="clause-picker">
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="relative flex-1">
              <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher une clause…" aria-label="Rechercher une clause" className="h-10 pl-9" data-testid="clause-picker-search" />
            </div>
            <Select value={theme} onValueChange={setTheme}>
              <SelectTrigger className="h-10 sm:w-52" aria-label="Thème" data-testid="clause-picker-theme"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les thèmes</SelectItem>
                {themes.map((t) => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
          {!items && !error && <div className="space-y-2"><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /></div>}
          {items && items.length === 0 && (
            <p className="rounded-md border border-dashed border-slate-300 p-4 text-center text-sm text-slate-500" data-testid="clause-picker-empty">
              {search || theme !== "all" ? "Aucune clause ne correspond." : "La bibliothèque est vide : écrivez et publiez d'abord une clause."}
            </p>
          )}
          {items && items.length > 0 && (
            <ul className="max-h-56 divide-y divide-slate-100 overflow-y-auto rounded-md border border-slate-200" role="listbox" aria-label="Clauses publiées">
              {items.map((c) => (
                <li key={c.id}>
                  <button type="button" role="option" aria-selected={chosen?.id === c.id} onClick={() => choose(c)} data-testid={`clause-picker-${c.id}`}
                    className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-slate-50 ${chosen?.id === c.id ? "bg-jamora-50" : ""}`}>
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium text-slate-900">{c.name}</span>
                      <span className="block truncate text-xs text-slate-500">{c.theme_label}{c.description ? ` · ${c.description}` : ""}</span>
                    </span>
                    <span className="shrink-0 text-xs text-slate-500">Version {c.published_number}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {busy && <Skeleton className="h-24 w-full" />}
          {chosen && !busy && (
            <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="clause-picker-preview">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{chosen.name} · version {chosen.published.number}</p>
              <div className="max-h-48 overflow-y-auto"><ClauseText blocks={chosen.published.blocks} /></div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Fermer</Button>
          <Button disabled={!chosen || busy} onClick={() => onPick(clauseBlockOf(chosen))} data-testid="clause-picker-insert">
            {chosen ? `Insérer la version ${chosen.published.number}` : "Insérer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// « Mettre à jour »: the version the template holds beside the latest published one, line by line, before anything changes.
export function ClauseCompareDialog({ block, onClose, onConfirm }) {
  const [latest, setLatest] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!block) return;
    setLatest(null); setError("");
    api.get(`/records/clauses/${block.clause_id}`).then(({ data }) => setLatest(data)).catch((e) => setError(recordsError(e, "Clause introuvable.")));
  }, [block]);
  const ops = useMemo(() => (block && latest?.published ? lineDiff(clauseLines(block.clause_blocks), clauseLines(latest.published.blocks)) : []), [block, latest]);
  const column = (kind) => ops.filter((o) => o.type === "same" || o.type === kind);
  const newer = latest?.published && latest.published.number > block?.clause_number;
  return (
    <Dialog open={!!block} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Mettre à jour la clause</DialogTitle>
          <DialogDescription>{block?.clause_name} : comparez les deux versions. Rien ne change dans le modèle avant « Mettre à jour ».</DialogDescription>
        </DialogHeader>
        {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
        {!latest && !error && <Skeleton className="h-40 w-full" />}
        {block && latest?.published && (
          <div className="grid gap-3 md:grid-cols-2" data-testid="clause-compare">
            {[["del", `Version ${block.clause_number} · dans le modèle`, "bg-red-50 text-red-900"], ["add", `Version ${latest.published.number} · publiée`, "bg-emerald-50 text-emerald-900"]].map(([kind, title, tone]) => (
              <div key={kind} className="space-y-1.5" data-testid={`clause-compare-${kind}`}>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</p>
                <ul className="max-h-64 space-y-1 overflow-y-auto rounded-md border border-slate-200 p-2 text-sm">
                  {column(kind).map((o, i) => <li key={i} className={`rounded px-1.5 py-0.5 ${o.type === "same" ? "text-slate-700" : tone}`}>{o.text}</li>)}
                </ul>
              </div>
            ))}
          </div>
        )}
        {block && latest?.published && <p className="text-xs text-slate-500" data-testid="clause-compare-count">{changedCount(ops)} ligne{changedCount(ops) > 1 ? "s" : ""} différente{changedCount(ops) > 1 ? "s" : ""}.</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Garder la version {block?.clause_number}</Button>
          <Button disabled={!newer} onClick={() => onConfirm(clauseBlockOf(latest))} data-testid="clause-compare-confirm">Mettre à jour vers la version {latest?.published?.number || ""}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DetachClauseDialog({ block, onClose, onConfirm }) {
  return (
    <AlertDialog open={!!block} onOpenChange={(v) => !v && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Détacher la clause « {block?.clause_name} » ?</AlertDialogTitle>
          <AlertDialogDescription>
            Son texte devient des blocs ordinaires de ce modèle, que vous pouvez modifier. Le lien avec la bibliothèque disparaît : les prochaines versions de la clause ne seront plus proposées, et cette action est définitive.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Garder la clause</AlertDialogCancel>
          <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={onConfirm} data-testid="clause-detach-confirm">Détacher</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}


